import React, { useCallback, useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import MonthGrid from "../MonthGrid/MonthGrid"
import { buildMonthDays, fmtDate, MONTHS, sameDay } from "../../utils/constants"
import { useTranslation } from "react-i18next"

const RangePicker = ({ id, openId, setOpenId, label, value, onChange }) => {
  const { t } = useTranslation()
  const open = openId === id
  const triggerRef = useRef(null)
  const dropRef = useRef(null)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() })
  const [hovered, setHovered] = useState(null)
  const [pos, setPos] = useState({ top: 0, left: 0, dual: true, width: 480 })
  const { from, to } = value

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    const dual = window.innerWidth >= 640
    const width = dual
      ? Math.min(480, window.innerWidth - 24)
      : Math.min(260, window.innerWidth - 24)
    const dropH = dual ? 290 : 330
    const spaceBelow = window.innerHeight - r.bottom
    const top =
      spaceBelow < dropH ? r.top + window.scrollY - dropH - 4 : r.bottom + window.scrollY + 4
    const left = Math.min(r.left + window.scrollX, window.innerWidth - width - 8)
    setPos({ top, left: Math.max(8, left), dual, width })
  }, [])

  useEffect(() => {
    if (!open) return
    calcPos()
    window.addEventListener("resize", calcPos)
    return () => window.removeEventListener("resize", calcPos)
  }, [open, calcPos])

  useEffect(() => {
    if (!open) return
    const h = (e) => {
      if (triggerRef.current?.contains(e.target)) return
      if (dropRef.current?.contains(e.target)) return
      setOpenId(null)
    }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [open, setOpenId])

  const m1 = view
  const m2 = view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 }
  const handleDay = (d) => {
    if (!from || to || d < from) onChange({ from: d, to: null })
    else if (sameDay(d, from)) onChange({ from: null, to: null })
    else {
      onChange({ from, to: d })
      setOpenId(null)
    }
  }
  const hasVal = from || to
  const display =
    from && to ? `${fmtDate(from)} — ${fmtDate(to)}` : from ? `${fmtDate(from)} — ...` : ""
  const gridProps = {
    from,
    to,
    hovered,
    onDay: handleDay,
    onHover: setHovered,
    onLeave: () => setHovered(null),
  }
  return (
    <>
      <div ref={triggerRef} className="w-full">
        <button
          type="button"
          onClick={() => setOpenId(open ? null : id)}
          className={`w-full flex items-center gap-2 px-2.5 py-2 bg-white border rounded-xl text-xs transition-all focus:outline-none
            ${open ? "border-blue-400 ring-2 ring-blue-500/20" : "border-gray-200 hover:border-gray-300"}`}
        >
          <svg
            className="w-3.5 h-3.5 flex-shrink-0 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span
            className={`flex-1 truncate text-left ${hasVal ? "text-gray-700" : "text-gray-400"}`}
          >
            {display || label}
          </span>
          {hasVal && (
            <span
              onMouseDown={(e) => {
                e.stopPropagation()
                onChange({ from: null, to: null })
              }}
              className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </span>
          )}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={dropRef}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              zIndex: 9999,
              width: pos.width,
            }}
            className="bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 select-none"
          >
            {/* Navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() =>
                  setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))
                }
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              {pos.dual ? (
                <div className="flex flex-1">
                  <span className="flex-1 text-center text-sm font-semibold text-gray-800">
                    {MONTHS[m1.m]} {m1.y}
                  </span>
                  <span className="flex-1 text-center text-sm font-semibold text-gray-800">
                    {MONTHS[m2.m]} {m2.y}
                  </span>
                </div>
              ) : (
                <span className="flex-1 text-center text-sm font-semibold text-gray-800">
                  {MONTHS[m1.m]} {m1.y}
                </span>
              )}
              <button
                type="button"
                onClick={() =>
                  setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))
                }
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            {/* Grids */}
            {pos.dual ? (
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <MonthGrid days={buildMonthDays(m1.y, m1.m)} {...gridProps} />
                <div
                  style={{ width: 1, background: "#f1f5f9", alignSelf: "stretch", flexShrink: 0 }}
                />
                <MonthGrid days={buildMonthDays(m2.y, m2.m)} {...gridProps} />
              </div>
            ) : (
              <MonthGrid days={buildMonthDays(m1.y, m1.m)} {...gridProps} />
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">
                {" "}
                {from && to
                  ? `${fmtDate(from)} — ${fmtDate(to)}`
                  : from
                    ? t("filter.select_end_date")
                    : t("filter.select_start_date")}{" "}
              </span>
              <button
                type="button"
                onClick={() => onChange({ from: null, to: null })}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors"
              >
                {t("admin.reset")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default RangePicker
